import React, { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  getAllDomainsBySubject,
  getDomainById,
} from "../../../api/domain.service";
import constant from "../../../utils/constant";
import {
  getStandards,
  getStandardsByDomainId,
} from "../../../api/standard.service";
import { IStandard } from "../../../model/interface/standard";
import { IDomain } from "../../../model/interface/domain";
import {
  PencilIcon,
  DownArrowIcon,
  UpArrowIcon,
  AddStandardIcon,
  QuestionCircleIcon,
  WorksheetIcon,
  VideoIcon,
  AlertIcon,
} from "../../../assets/icons/index";
import Loader from "../../shared/loader/loader";
import DomainTestPercentage from "./domainTestPercentage";
import { IAutoFillData } from "../../../model/interface/autoFillData";

interface ManageDomainsAndStandardProps {
  subjectId?: number;
  domainId?: number;
  showTestPercent: boolean;
  autoFillData?: IAutoFillData;
  showTestPercentAlert?: boolean;
}
interface DomainColorCodes {
  domainId: number;
  colour: string;
  percent: number;
  isAdditional: boolean;
  loadOrder: number;
}

function ManageDomainsAndStandards(props: ManageDomainsAndStandardProps) {
  const [newDomain, setNewDomain] = useState<IDomain[]>([]);
  const [standards, setStandards] = useState<IStandard[]>([]);
  const [allStandards, setAllStandards] = useState<IStandard[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [isExpandAll, setIsExpandAll] = useState<boolean>(true);
  const [selectedDomain, setSelectedDomain] = useState<number>(0);
  const [domainSumPercent, setDomainSumPercent] = useState<number>(0);
  const [domainColourCodes, setDomainColourCodes] = useState<
    DomainColorCodes[]
  >([]);
  const navigate = useNavigate();

  const bindDomain = () => {
    return new Promise((resolve) => {
      setLoading(true);
      if (props.subjectId) {
        getAllDomainsBySubject(props.subjectId).then((response: any) => {
          setLoading(false);

          setDomainSumPercent(
            response.data.data
              .map((item) => {
                return item.testPercentage;
              })
              .reduce((sum, item) => sum + item, 0)
          );

          setNewDomain(response.data.data);
          resolve(response.data.data);
        });
      } else if (!props.subjectId && props.domainId && props.domainId > 0) {
        getDomainById(props.domainId).then((response: any) => {
          setLoading(false);
          setNewDomain([response.data]);
          resolve(true);
        });
      } else {
        setLoading(false);
        resolve(false);
      }
    });
  };

  useEffect(() => {
    buildDomainColourCode(newDomain);
  }, [newDomain]);

  const getRandomColour = () => {
    return (
      "rgb(" +
      Math.floor(Math.random() * 256) +
      "," +
      Math.floor(Math.random() * 256) +
      "," +
      Math.floor(Math.random() * 256) +
      ")"
    );
  };

  const handleExpandAll = () => {
    setAllStandards([]);
    setIsExpandAll(!isExpandAll);
    bindAllStands();
  };

  const bindAllStands = (domainsData?) => {
    const domains = domainsData && domainsData.length ? domainsData : newDomain;
    setLoading(true);
    const promises: any = [];
    setAllStandards([]);
    for (let i = 0; i < domains.length; i++) {
      promises.push(setStandardsData(domains[i].domainId));
    }
    Promise.all(promises).then((result: any) => {
      const merged = [].concat([], ...result);
      setAllStandards(merged);
      setLoading(false);
    });
  };

  const buildDomainColourCode = (domains: any[]) => {
    const domainColourCodesTemp: DomainColorCodes[] = [];
    let totalPercent = 0;

    for (const domain of domains) {
      totalPercent = totalPercent + domain.testPercentage;
      const ColourObject = {
        domainId: domain.domainId,
        colour: getRandomColour(),
        percent: domain.testPercentage,
        isAdditional: false,
        loadOrder: domain.loadOrder,
      };
      domainColourCodesTemp.push(ColourObject);
    }
    if (totalPercent < 100) {
      domainColourCodesTemp.push({
        domainId: 0,
        colour: "#F3F4F6",
        percent: 100 - totalPercent,
        isAdditional: true,
        loadOrder: 0,
      });
    }

    setDomainColourCodes(domainColourCodesTemp);
  };

  const setStandardsData = async (domainId: number) => {
    return new Promise((resolve) => {
      const standard: IStandard = {
        name: "",
        contentAreaId: "",
        subjectId: props.domainId?.toString()
          ? ""
          : props?.subjectId?.toString() || "",
        domainId: domainId.toString(),
        code: "",
        isActive: true,
        isApproved: true,
        description: "",
        loadOrder: 0,
        pageSize: 999,
        pageNumber: 1,
        orderbyColumn: "",
        sortDir: "acs",
      };
      getStandards(standard).then((response: any) => {
        setStandards(response.data.data);
        resolve(response.data.data);
      });
    });
  };

  const handleDomainClickHandler = (domainId: number) => {
    if (domainId) {
      setLoading(true);

      setSelectedDomain(domainId);

      getStandardsByDomainId(domainId).then((response: any) => {
        setStandards(response.data.data);
        setLoading(false);
      });
    } else {
      setSelectedDomain(0);
      setIsExpandAll(false);
      setIsCollapsed(!isCollapsed);
      setStandards([]);
    }
  };

  const getDomainColour = (domainId: number) => {
    return domainColourCodes.filter((ele) => {
      return ele.domainId == domainId;
    })[0]?.colour;
  };

  const handleRedirect = (
    objectToEdit: string,
    objectId: number,
    autoFillParam?: number,
    activeTab?: string
  ) => {
    if (objectId) {
      navigate(
        objectToEdit === "domain"
          ? constant.EDITDOMAIN.replace(":id", objectId.toString())
          : constant.EDITSTANDARD.replace(":id", objectId.toString()),
        { state: { activeTab: activeTab, redirectBack: true } }
      );
    } else {
      const autoFillDataTemp = props.autoFillData || {};
      if (autoFillParam) {
        autoFillDataTemp["domainId"] = autoFillParam;
      }
      navigate(
        objectToEdit === "domain" ? constant.ADDDOMAIN : constant.ADDSTANDARD,
        { state: { autoFillData: autoFillDataTemp, redirectBack: true } }
      );
    }
  };

  const formatStandardCode = (code: string) => {
    let strArr = code.split(",");
    strArr = strArr.map((ele) => {
      return `[${ele.trim()}]`;
    });

    return strArr.toString();
  };

  useEffect(() => {
    const syncCalls = async () => {
      const domainsData = await bindDomain();
      bindAllStands(domainsData);
      if (props.domainId) setStandardsData(props.domainId);
    };
    syncCalls();
  }, []);

  return (
    <Fragment>
      {loading && <Loader />}
      <div className="w-3/5 pl-3">
        {props?.showTestPercent && props?.showTestPercentAlert && (
          <div className="rounded-md bg-yellow-50 p-4 mb-7">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertIcon />
              </div>
              <div className="ml-3">
                <div className="text-sm text-yellow-700">
                  <span>
                    The subject cannot be live if the domain is not 100%,
                    currently it is {domainSumPercent}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div>
          {props.showTestPercent && (
            <div
              className={`flex items-center pb-2 ${
                props.domainId ? "hidden" : ""
              }`}
            >
              <div className="w-full flex">
                {newDomain.length > 0 &&
                  domainColourCodes.map((colourCode) => (
                    <DomainTestPercentage
                      key={colourCode.domainId}
                      colour={colourCode.colour}
                      percent={colourCode.percent}
                      isAdditional={colourCode.isAdditional}
                    />
                  ))}
              </div>
            </div>
          )}

          <div className="px-3 py-3 dark:text-white">
            <div className="flex justify-between">
              {!props.domainId && (
                <div className="flex w-full justify-between items-center mt-1 mb-4 rounded-lg">
                  <button
                    onClick={() => {
                      handleRedirect("domain", 0);
                    }}
                    type="button"
                    className="flex items-center text-sm dark:text-white  cursor-pointer"
                  >
                    <AddStandardIcon />
                    Add Domain
                  </button>
                </div>
              )}
              {newDomain.length > 1 && (
                <div className="flex justify-end items-center mb-4">
                  <span
                    onClick={() => handleExpandAll()}
                    className="flex items-center px-3 w-auto whitespace-nowrap inline-block py-1.5  cursor-pointer hover:text-gray-500 text-sm"
                  >
                    {isExpandAll ? "Collapse All" : "Expand All"}
                    {!isExpandAll ? (
                      <DownArrowIcon className="h-5 w-5 ml-1" />
                    ) : (
                      <UpArrowIcon className="h-5 w-5 ml-1" />
                    )}
                  </span>
                </div>
              )}
            </div>

            {newDomain
              .sort((a, b) =>
                a.loadOrder === b.loadOrder
                  ? -1
                  : a.loadOrder > b.loadOrder
                  ? 1
                  : -1
              )
              .map((domain, index) => (
                <div
                  key={index}
                  className={`flex py-3 px-3 mb-2  rounded-lg bg-white border`}
                >
                  <div
                    className="w-2 mr-2 rounded-lg"
                    style={{
                      backgroundColor: `${getDomainColour(domain.domainId)}`,
                    }}
                  ></div>

                  <div className="w-full">
                    <div className="flex items-center justify-between border-b pb-2 mb-2 border-gray-200">
                      {!props.domainId ? (
                        <div className="flex items-center">
                          <Link
                            to={constant.EDITDOMAIN.replace(
                              ":id",
                              domain.domainId.toString()
                            )}
                            className="rounded cursor-pointer hover:underline bg-gray-100 border p-1 text-sm mr-2 text-primary-color dark:text-white"
                          >
                            {domain.domainId}
                          </Link>
                          <Link
                            to={constant.EDITDOMAIN.replace(
                              ":id",
                              domain.domainId.toString()
                            )}
                            className="rounded cursor-pointer hover:underline bg-gray-100 border p-1 text-sm mr-2 text-primary-color dark:text-white"
                          >
                            {domain.name}
                          </Link>
                          <Link
                            to={constant.EDITDOMAIN.replace(
                              ":id",
                              domain.domainId.toString()
                            )}
                            className="rounded cursor-pointer hover:underline bg-gray-100 border p-1 text-sm mr-2 text-primary-color dark:text-white"
                          >
                            <PencilIcon />
                          </Link>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <span className="rounded  bg-gray-100 border p-1 text-sm mr-2">
                            {domain.domainId}
                          </span>
                          <span className="text-primary-color dark:text-white  font-medium">
                            {domain.name}
                          </span>
                        </div>
                      )}

                      <div className="flex">
                        <span
                          id="DownArrow"
                          className="flex items-center justify-center  text-gray-700 rounded-full h-5 w-5 cursor-pointer hover:bg-opacity-90"
                        >
                          {isExpandAll || selectedDomain === domain.domainId ? (
                            <DownArrowIcon
                              className={`h-5 w-5`}
                              onClick={() => {
                                handleDomainClickHandler(0);
                              }}
                            />
                          ) : (
                            <UpArrowIcon
                              className={`h-5 w-5`}
                              onClick={() => {
                                handleDomainClickHandler(domain.domainId);
                              }}
                            />
                          )}
                        </span>
                      </div>
                    </div>
                    {standards
                      .sort((a, b) =>
                        a.loadOrder === b.loadOrder
                          ? -1
                          : a.loadOrder > b.loadOrder
                          ? 1
                          : -1
                      )
                      .map((stand, index) => (
                        <div
                          key={index}
                          className={`${
                            props.domainId
                              ? ""
                              : !isExpandAll &&
                                selectedDomain === domain.domainId
                              ? ""
                              : "hidden"
                          } `}
                        >
                          <div
                            className={`flex items-center justify-between mb-2 pl-4 mt-1 `}
                          >
                            <div className="flex items-center">
                              <Link
                                target="_blank"
                                to={constant.EDITSTANDARD.replace(
                                  ":id",
                                  stand?.standardId?.toString()
                                )}
                                className={`rounded cursor-pointer hover:underline  p-1 text-sm mr-2 text-primary-color dark:text-white`}
                              >
                                {stand.standardId}
                              </Link>
                              <Link
                                target="_blank"
                                to={constant.EDITSTANDARD.replace(
                                  ":id",
                                  stand?.standardId?.toString()
                                )}
                                className={`rounded cursor-pointer hover:underline  p-1 text-sm mr-2 text-primary-color dark:text-white`}
                              >
                                {`${formatStandardCode(stand.code)}`}
                              </Link>
                              <Link
                                target="_blank"
                                to={constant.EDITSTANDARD.replace(
                                  ":id",
                                  stand?.standardId?.toString()
                                )}
                                className={`rounded cursor-pointer hover:underline  p-1 text-sm mr-2 text-primary-color dark:text-white`}
                              >
                                {stand.name}
                              </Link>
                              <Link
                                to={constant.EDITSTANDARD.replace(
                                  ":id",
                                  stand?.standardId?.toString()
                                )}
                                className={`rounded cursor-pointer hover:underline  p-1 text-sm mr-2 text-primary-color dark:text-white`}
                              >
                                <PencilIcon />
                              </Link>
                            </div>

                            <div className="flex space-x-3 ml-3 text-gray-600">
                              <span
                                onClick={() => {
                                  handleRedirect(
                                    "standard",
                                    stand.standardId,
                                    0,
                                    "AssociatedQuestions"
                                  );
                                }}
                                className="flex items-center text-primary-color cursor-pointer hover:underline border-primary-color"
                              >
                                <QuestionCircleIcon className="w-3 h-3 mr-1 text-gray-400 fill-current  hover:text-gray-500" />
                                <span>{stand.totalQuestions}</span>
                              </span>

                              <span
                                onClick={() => {
                                  handleRedirect(
                                    "standard",
                                    stand.standardId,
                                    0,
                                    "Worksheets"
                                  );
                                }}
                                className="flex items-center text-primary-color cursor-pointer hover:underline border-primary-color"
                              >
                                <WorksheetIcon className="w-4 h-4 mr-1 text-gray-400 stroke-current  hover:text-gray-500" />
                                <span>{stand.totalWorksheets}</span>
                              </span>

                              <span
                                onClick={() => {
                                  handleRedirect(
                                    "standard",
                                    stand.standardId,
                                    0,
                                    "Videos"
                                  );
                                }}
                                className="flex items-center text-primary-color cursor-pointer hover:underline border-primary-color"
                              >
                                <VideoIcon className="w-4 h-4 mr-1 text-gray-400 fill-current  hover:text-gray-500" />
                                <span>{stand.totalVideos}</span>
                              </span>
                            </div>
                          </div>
                          <div
                            className={`flex items-center mb-2 pl-4 ${
                              stand.domainId == domain.domainId.toString()
                                ? ""
                                : "hidden"
                            }`}
                          >
                            <span
                              className={`mb-2 bg-gray-50 p-2 text-xs mr-2 pl-19`}
                            >
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: stand.description,
                                }}
                              />
                            </span>
                          </div>
                        </div>
                      ))}
                    {isExpandAll &&
                      allStandards
                        .sort((a, b) =>
                          a.loadOrder === b.loadOrder
                            ? -1
                            : a.loadOrder > b.loadOrder
                            ? 1
                            : -1
                        )
                        .map((stand, index) => (
                          <div key={index}>
                            <div
                              className={`flex items-center justify-between pl-4 mt-1 ${
                                domain.domainId.toString() ===
                                stand.domainId.toString()
                                  ? ""
                                  : "hidden"
                              }`}
                            >
                              <div className={`flex items-center  `}>
                                <Link
                                  target="_blank"
                                  to={constant.EDITSTANDARD.replace(
                                    ":id",
                                    stand?.standardId?.toString()
                                  )}
                                  className={`rounded cursor-pointer hover:underline  p-1 text-sm mr-2 text-primary-color dark:text-white`}
                                >
                                  {stand.standardId}
                                </Link>
                                <Link
                                  target="_blank"
                                  to={constant.EDITSTANDARD.replace(
                                    ":id",
                                    stand?.standardId?.toString()
                                  )}
                                  className="rounded cursor-pointer hover:underline  p-1 text-sm mr-2 text-primary-color dark:text-white"
                                >
                                  {`${formatStandardCode(stand.code)}`}
                                </Link>
                                <Link
                                  target="_blank"
                                  to={constant.EDITSTANDARD.replace(
                                    ":id",
                                    stand?.standardId?.toString()
                                  )}
                                  className="text-primary-color cursor-pointer hover:underline border-primary-color dark:text-white"
                                >
                                  {stand.name}
                                </Link>
                                <Link
                                  to={constant.EDITSTANDARD.replace(
                                    ":id",
                                    stand?.standardId?.toString()
                                  )}
                                >
                                  <PencilIcon />
                                </Link>
                              </div>
                              <div className="flex space-x-3 ml-3 text-gray-600">
                                <Link
                                  className="flex items-center cursor-pointer hover:underline text-gray-400"
                                  to={
                                    constant.EDITSTANDARD.replace(
                                      ":id",
                                      stand.standardId.toString()
                                    ) + "?activeTab=AssociatedQuestions"
                                  }
                                >
                                  <QuestionCircleIcon className="w-4 h-4 mr-1 text-gray-400 fill-current  hover:text-gray-500" />
                                  <span>{stand.totalQuestions}</span>
                                </Link>
                                <Link
                                  className="flex items-center cursor-pointer hover:underline text-gray-400"
                                  to={
                                    constant.EDITSTANDARD.replace(
                                      ":id",
                                      stand.standardId.toString()
                                    ) + "?activeTab=Worksheets"
                                  }
                                >
                                  <WorksheetIcon className="w-4 h-4 mr-1 text-gray-400 stroke-current  hover:text-gray-500" />
                                  <span>{stand.totalWorksheets}</span>
                                </Link>
                                <Link
                                  className="flex items-center cursor-pointer hover:underline text-gray-400"
                                  to={
                                    constant.EDITSTANDARD.replace(
                                      ":id",
                                      stand.standardId.toString()
                                    ) + "?activeTab=Videos"
                                  }
                                >
                                  <VideoIcon className="w-4 h-4 mr-1 text-gray-400 fill-current  hover:text-gray-500" />
                                  <span>{stand.totalVideos}</span>
                                </Link>
                              </div>
                            </div>
                            <div
                              className={`flex items-center mb-2 pl-4 ${
                                stand.domainId == domain.domainId.toString()
                                  ? ""
                                  : "hidden"
                              }`}
                            >
                              <span
                                className={`mb-2 bg-gray-50 p-2 text-xs mr-2 pl-19`}
                              >
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: stand.description,
                                  }}
                                />
                              </span>
                            </div>
                          </div>
                        ))}

                    <div className=" pt-1 mt-1 pl-4">
                      <div className="flex">
                        <span
                          onClick={() => {
                            handleRedirect("standard", 0, domain.domainId);
                          }}
                          className="text-sm hover:underline cursor-pointer"
                        >
                          <AddStandardIcon />
                          Add Standard
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default ManageDomainsAndStandards;
